import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Drawer } from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { activeOnly } from "@/lib/users.js";
import { Field, Input, SwitchRow, Textarea } from "@/components/ui/Form.jsx";
import { Avatar, Callout } from "@/components/ui/Display.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { idOf } from "@/lib/projects.js";
import { cx } from "@/lib/cx.js";

/*
 * Create / edit a project.
 *
 *   create  POST  /api/projects       { name, description, code, budgetHours,
 *                                       billable, teams, members }
 *   edit    PATCH /api/projects/:id   the same, plus isArchived
 *
 * Teams and members are picked here rather than in a separate drawer, unlike
 * Teams: a project's membership is a handful of names chosen once at setup, not
 * an ongoing roster.
 */
const schema = z.object({
    name: z.string().trim().min(1, "Please enter a project name"),
    description: z.string().trim().default(""),
    code: z
        .string()
        .trim()
        .default("")
        .refine((v) => !v || /^[A-Za-z0-9-]+$/.test(v), {
            message: "Letters, numbers and hyphens only",
        }),
    // An empty budget is "not planned", which the API stores as null.
    budgetHours: z
        .string()
        .default("")
        .refine((v) => !v || (Number(v) >= 0 && Number.isFinite(Number(v))), {
            message: "Enter a number of hours, or leave it empty",
        }),
    billable: z.boolean().default(false),
});

/*
 * Mounted only while open (see Projects.jsx), so every field starts from the
 * project being edited rather than being reset by an effect.
 */
export default function ProjectFormDrawer({
    project,
    teams = [],
    users = [],
    onClose,
    onSubmit,
    saving,
    error,
}) {
    const editing = Boolean(project?._id);

    const [selectedTeams, setSelectedTeams] = useState(() =>
        (project?.teams ?? []).map(idOf).filter(Boolean),
    );
    const [selectedMembers, setSelectedMembers] = useState(() =>
        (project?.members ?? []).map(idOf).filter(Boolean),
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            name: project?.name ?? "",
            description: project?.description ?? "",
            code: project?.code ?? "",
            budgetHours: project?.budgetHours != null ? String(project.budgetHours) : "",
            billable: Boolean(project?.billable),
        },
    });

    const toggle = (setter) => (id) =>
        setter((current) =>
            current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
        );

    const submit = (values) =>
        onSubmit({
            ...values,
            // "" means clear it; the API distinguishes that from "not sent".
            budgetHours: values.budgetHours === "" ? null : Number(values.budgetHours),
            teams: selectedTeams,
            members: selectedMembers,
        });

    return (
        <Drawer
            open
            onClose={onClose}
            title={editing ? "Edit project" : "New project"}
            subtitle={editing ? project.name : "Track hours against a piece of work"}
            size="lg"
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        form="project-form"
                        loading={saving}
                        data-save-project
                    >
                        {editing ? "Save changes" : "Create project"}
                    </Button>
                </>
            }
        >
            <form id="project-form" onSubmit={handleSubmit(submit)} noValidate className="space-y-4">
                {error && <Callout tone="danger">{error}</Callout>}

                <Field label="Project name" htmlFor="p-name" error={errors.name?.message}>
                    <Input id="p-name" data-autofocus autoComplete="off" {...register("name")} />
                </Field>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field
                        label="Code"
                        htmlFor="p-code"
                        optional
                        error={errors.code?.message}
                        hint="A short reference like WEB-01. Unique in your organization."
                    >
                        <Input
                            id="p-code"
                            className="font-mono uppercase"
                            autoCapitalize="characters"
                            placeholder="WEB-01"
                            {...register("code")}
                        />
                    </Field>

                    <Field
                        label="Budget hours"
                        htmlFor="p-budget"
                        optional
                        error={errors.budgetHours?.message}
                        hint="Leave empty for no budget — the bar then shows hours only."
                    >
                        <Input
                            id="p-budget"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="400"
                            {...register("budgetHours")}
                        />
                    </Field>
                </div>

                <Field label="Description" htmlFor="p-description" optional>
                    <Textarea id="p-description" rows={3} {...register("description")} />
                </Field>

                <SwitchRow
                    label="Billable"
                    hint="Time on this project is charged to a client."
                    {...register("billable")}
                />

                {/* ---- Teams ---- */}
                <div>
                    <p className="mb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                        Teams
                    </p>
                    {teams.length ? (
                        <div className="flex flex-wrap gap-1.5">
                            {teams.map((team) => {
                                const on = selectedTeams.includes(team._id);
                                return (
                                    <button
                                        key={team._id}
                                        type="button"
                                        data-team={team._id}
                                        aria-pressed={on}
                                        onClick={() => toggle(setSelectedTeams)(team._id)}
                                        className={cx(
                                            "tag transition",
                                            on
                                                ? "bg-brand-600 text-white"
                                                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700",
                                        )}
                                    >
                                        {team.name}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-[13px] text-slate-400 dark:text-slate-500">
                            No teams yet.
                        </p>
                    )}
                </div>

                {/* ---- Members ---- */}
                <div>
                    <p className="mb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                        Members
                        <span className="ml-2 font-normal tracking-normal normal-case">
                            {selectedMembers.length} selected
                        </span>
                    </p>

                    <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-800">
                        {activeOnly(users).map((user) => (
                            <label
                                key={user._id}
                                data-member={user._id}
                                className="flex min-w-0 cursor-pointer items-center gap-3 border-b border-slate-100 px-3.5 py-2 transition last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedMembers.includes(user._id)}
                                    onChange={() => toggle(setSelectedMembers)(user._id)}
                                    className="size-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-slate-600"
                                />
                                <Avatar name={user.name} size="sm" />
                                <span className="min-w-0 flex-1 truncate text-[13px]">
                                    {user.name}
                                </span>
                                {!user.isActive && <Tag tone="slate">Inactive</Tag>}
                            </label>
                        ))}
                    </div>
                </div>
            </form>
        </Drawer>
    );
}
